/**
 * Surface-mode resolution + persistence for the builder shell. Pure module
 * (unit-testable): the shell decides availability (host.document present —
 * the server kill-switch simply omits the capability), this module only
 * remembers the user's last choice per storage prefix.
 */

import { inject, provide, ref } from 'vue';
import type { InjectionKey, Ref } from 'vue';
import type { BuilderHost, BuilderSurface } from '../host';

export interface SurfaceStorage {
    getItem(key: string): string | null;
    setItem(key: string, value: string): void;
}

/** Shell-level surface state shared with the toggle in both bars. */
export interface SurfaceController {
    surface: Ref<BuilderSurface>;
    /** False = host has no document capability; the toggle renders nothing. */
    available: boolean;
    set(surface: BuilderSurface): void;
}

const SURFACE_KEY: InjectionKey<SurfaceController> = Symbol('builder-v2-surface');

/**
 * Creates + provides the surface controller for a builder instance. Hosts
 * without the document capability stay pinned to canvas regardless of any
 * persisted choice (kill-switch precedence).
 *
 * @param host    The builder host.
 * @param storage Storage backend; defaults to window.localStorage.
 * @return The surface controller.
 */
export function createSurfaceController(
    host: BuilderHost,
    storage: SurfaceStorage | null = defaultStorage(),
): SurfaceController {
    const prefix = host.storagePrefix ?? 'pb2';
    const available = Boolean(host.document);
    const surface = ref<BuilderSurface>(
        available ? resolveSurfaceMode(prefix, host.document?.default ?? 'canvas', storage) : 'canvas',
    );

    const controller: SurfaceController = {
        surface,
        available,
        set(next) {
            if (!available || surface.value === next) {
                return;
            }

            surface.value = next;
            persistSurfaceMode(prefix, next, storage);
        },
    };

    provide(SURFACE_KEY, controller);

    return controller;
}

export function useSurfaceControllerOptional(): SurfaceController | null {
    return inject(SURFACE_KEY, null);
}

const surfaceKey = (prefix: string): string => `${prefix}:surface:v1`;

/**
 * Resolves the surface to open: persisted user choice, else the host
 * default. Callers must not invoke this when the capability is absent.
 *
 * @param prefix      localStorage namespace (host storagePrefix).
 * @param hostDefault Surface the host opens without a persisted choice.
 * @param storage     Storage backend; defaults to window.localStorage.
 * @return The surface to mount.
 */
export function resolveSurfaceMode(
    prefix: string,
    hostDefault: BuilderSurface,
    storage: SurfaceStorage | null = defaultStorage(),
): BuilderSurface {
    try {
        const stored = storage?.getItem(surfaceKey(prefix));

        if (stored === 'document' || stored === 'canvas') {
            return stored;
        }
    } catch {
        // private mode / quota — fall through to the host default
    }

    return hostDefault;
}

/**
 * Persists the user's surface choice.
 *
 * @param prefix  localStorage namespace (host storagePrefix).
 * @param surface Chosen surface.
 * @param storage Storage backend; defaults to window.localStorage.
 * @return void No return expected.
 */
export function persistSurfaceMode(
    prefix: string,
    surface: BuilderSurface,
    storage: SurfaceStorage | null = defaultStorage(),
): void {
    try {
        storage?.setItem(surfaceKey(prefix), surface);
    } catch {
        // private mode / quota — the choice just won't stick
    }
}

function defaultStorage(): SurfaceStorage | null {
    return typeof localStorage === 'undefined' ? null : localStorage;
}
